<template>
  <DialogHeader>
    <div>Make selected files public</div>
    <Pill v-if="isBasic" class="ml-3" success>Pro only</Pill>
  </DialogHeader>
  <DialogContent>
    <UpgradeToProCallout v-if="isBasic" class="mb-5">
      Enable privacy/invite controls and other features
    </UpgradeToProCallout>

    <div :class="containerClasses">
      You are about to change the selected files to be public. Anyone will be
      able to view these files and their descriptions. This may take a few
      moments to complete.
    </div>
  </DialogContent>
  <DialogFooter>
    <UiButton
      class="w-1/2 tablet-h:w-auto mr-2.5 tablet-h:mr-2"
      :disabled="loading || isBasic"
      @click="close()"
    >
      Cancel
    </UiButton>
    <UiButton
      class="w-1/2 tablet-h:w-auto"
      :disabled="isBasic"
      :loading="loading"
      primary
      @click="handleClick"
    >
      Make public
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import { Pill, UiButton } from '@dacr/ui'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import {
  UserPermission,
  useUpdateFilePrivacyMutation,
} from '@/shared/graphql/legacy/generated'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { updateFileInExplorerCache } from '@/features/explorer/utils/explorerCache'
import gql from 'graphql-tag'
import { useUserStore } from '@/shared/stores/user'
import { computed } from 'vue'
import UpgradeToProCallout from '@/features/billing/components/UpgradeToProCallout.vue'

const { close, open } = useDialog()

const props = defineProps<{
  ids: string[]
}>()

const { mutate: updateFilePrivacy, loading } = useUpdateFilePrivacyMutation()

const userStore = useUserStore()
const toastStore = useToastStore()

const isBasic = computed(
  () => !userStore.user?.permissions.includes(UserPermission.Pro)
)

const containerClasses = computed(() => ({
  'pointer-events-none opacity-50': isBasic.value,
}))

const handleClick = async () => {
  try {
    const response = await updateFilePrivacy(
      {
        fileIds: props.ids,
        isPrivate: false,
      },
      {
        update(cache) {
          props.ids.forEach((id) => {
            updateFileInExplorerCache(id, {
              settings: { __typename: 'FileSettings', isPrivate: false },
            })
            cache.updateFragment(
              {
                id: `File:${id}`,
                fragment: gql`
                  fragment UpdatePrivacy on File {
                    id
                    isPrivate
                  }
                `,
              },
              (item) => {
                if (!item) return

                return {
                  ...item,
                  isPrivate: false,
                }
              }
            )
          })
        },
      }
    )

    if (response?.data?.updateFilePrivacy.asyncProcess) {
      open(Dialogs.Success, {
        props: {
          title: 'Bulk change successful',
          html: 'Note: In some cases, it may take several minutes to process your request.',
        },
      })
    } else {
      close()

      toastStore.showToast({
        type: ToastType.Success,
        content: 'Successfully updated file privacy settings',
      })
    }
  } catch (error) {
    toastStore.showToast({
      type: ToastType.Error,
      content: 'Could not update privacy settings',
    })
  }
}
</script>
